import type { IMessageConfig, ISystem } from '@/interfaces/gui'

/**
 * Start intent passed by the dinoConnect app in the URL when the user opens
 * the visualisation from a push notification, e.g. `?open=messages&status=alarm`.
 */
export interface IStartIntent {
  readonly open: 'messages'
  /** messageIcon status that triggered the push (alarm, fault, warning, service) */
  readonly status?: string
}

export function readStartIntent(search: string = window.location.search): IStartIntent | null {
  const params = new URLSearchParams(search)
  if (params.get('open') !== 'messages') return null
  const status = params.get('status')?.toLowerCase() || undefined
  return { open: 'messages', status }
}

// Message types that can cause a given messageIcon status
const MESSAGE_TYPES_FOR_STATUS: Record<string, readonly string[]> = {
  alarm: ['alarm'],
  fault: ['fault', 'alarm'],
  warning: ['warning'],
  service: ['service'],
}

/** Same rule as MessageListItem: a message is active if its datapoint is truthy (or falsy for `!`). */
export function isMessageActive(message: IMessageConfig, datapointValue: unknown): boolean | undefined {
  if (datapointValue === undefined || datapointValue === null) return undefined
  const raw =
    typeof datapointValue === 'object' && 'value' in (datapointValue as Record<string, unknown>)
      ? (datapointValue as Record<string, unknown>).value
      : datapointValue
  return message.datapoint.startsWith('!') ? !raw : Boolean(raw)
}

export function cleanDatapoint(message: IMessageConfig): string {
  return message.datapoint.startsWith('!') ? message.datapoint.slice(1) : message.datapoint
}

/**
 * Picks the circuit (system) whose message caused the push.
 * Returns undefined when it cannot be determined; the caller then shows all circuits.
 */
export function pickSystemForStatus(
  systems: readonly ISystem[],
  messages: readonly IMessageConfig[],
  status: string | undefined,
  getDatapointValue: (datapoint: string) => unknown,
): string | undefined {
  if (systems.length === 1) return systems[0]?.name
  if (!status) return undefined

  const types = MESSAGE_TYPES_FOR_STATUS[status] ?? [status]
  const candidates = messages.filter((m) => types.includes(String(m.type).toLowerCase()))
  const active = candidates.filter((m) => isMessageActive(m, getDatapointValue(cleanDatapoint(m))))
  if (active.length === 0) return undefined

  // Newest active message wins
  const newest = [...active].sort((a, b) => (b.start ?? 0) - (a.start ?? 0))[0]
  return newest?.system
}
